import { Upload } from "lucide-react";
import React from "react";

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  ImageCropper,
} from "@/components";
import { useI18n } from "@/context/i18n";

type GenericField<T = unknown> = {
  value?: T;
  onChange?: (value: T) => void;
  onBlur?: () => void;
  name?: string;
  ref?: React.Ref<unknown>;
};

type TUploadAvatarProps<T = unknown> = {
  field?: GenericField<T>;
  file: File | undefined;
  onCropComplete: (croppedFile: File) => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  fallbackName?: string;
  imageSrc?: string;
};

export const UploadAvatar: React.FC<TUploadAvatarProps> = ({
  field,
  file,
  fileInputRef,
  onCropComplete,
  fallbackName,
  imageSrc,
}) => {
  const [isDialogOpen, setDialogOpen] = React.useState(false);
  const { t } = useI18n();
  return (
    <div className="mb-8">
      <div className="flex flex-col sm:flex-row items-center gap-6 mb-6">
        {file ? (
          <ImageCropper
            className="h-24 w-24"
            dialogOpen={isDialogOpen}
            setDialogOpen={setDialogOpen}
            selectedFile={file}
            fallbackName={fallbackName}
            onCropComplete={onCropComplete}
          />
        ) : (
          <Avatar className="h-24 w-24">
            <AvatarImage
              src={file ? URL.createObjectURL(file) : imageSrc}
              alt="Avatar"
              className="object-cover h-full w-full"
            />
            <AvatarFallback className="text-2xl">{fallbackName}</AvatarFallback>
          </Avatar>
        )}
        <div className="flex-1 mt-6">
          <input
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            className="hidden"
            ref={fileInputRef}
            onChange={(e) => {
              const selectedFile = e.target.files?.[0];
              if (selectedFile) {
                field?.onChange?.(selectedFile);
              }
            }}
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              fileInputRef.current?.click();
            }}
            className="mb-2"
          >
            <Upload className="h-4 w-4 mr-2" />
            {t("components.uploadAvatar.changePictureButton")}
          </Button>

          {file && <p className="text-sm text-muted-foreground">{file.name}</p>}

          <p className="text-sm text-muted-foreground mt-2 text-wrap">
            {t("components.uploadAvatar.changePictureButtonDescription")}
          </p>
        </div>
      </div>
    </div>
  );
};
